'use client';

import { getGroupLeagues, getSortedMatchCollection } from '@/utils/odds-utils';
import { DropdownIcon } from '@/components/icons/svg-icons';
import { useEffect, useRef, useState } from 'react';
import { TopbarMatchListOption } from './top-bar-match-list-option';
import { useOuterClick } from '@nf/utils-common/hooks';
import { useAtomValue } from 'jotai';
import { oddsPageAtom } from '@/atoms/odds-page';
import { IO } from '@nf/utils-common/fpw/monadic';
import { itemsEmptyCheck } from '@nf/utils-common/fpw/utils';
import { MatchGroup } from '@/features/odds-page/types';
import { useFilterSabaString } from '@/hooks/query/use-saba-filter';
import { SportMatchModel } from '@nf/types/odds';
import { useAccountInfo } from '@/hooks/query/account';

interface TopbarMatchListProps {
	matchId?: number;
	isListOpen: boolean;
	setIsListOpen: (isListOpen: boolean) => void;
}

export const TopbarMatchList = ({ matchId, isListOpen, setIsListOpen }: TopbarMatchListProps) => {
	const oddsData = useAtomValue(oddsPageAtom);
	const [leagueCollection, setLeagueCollection] = useState<MatchGroup[]>([]);
	const [openIndex, setOpenIndex] = useState(0);
	const fastChangeTips = useRef<HTMLDivElement>(null);
	const { filterSabaString } = useFilterSabaString();
	useOuterClick(fastChangeTips, () => setIsListOpen(false));
	let sortNames: Array<string> = ['MatchCode', 'LeagueId'];
	const { user } = useAccountInfo();
	if (user?.UserSetting.EventSortType === 0) { // sortByTime Setting
		sortNames = ['Etm', 'MatchCode'];
	}
	if (oddsData?.sportId == 43) {
		sortNames = ['GTY', ...sortNames];
	}

	const handleLeagueOpen = (index: number) => {
		if (openIndex === index) {
			setOpenIndex(-1);
		} else {
			setOpenIndex(index);
		}
	};

	const filterSabaStringToLeague = (matchGroup: MatchGroup[]) => {
		matchGroup.forEach((match) => {
			match.LeagueName = filterSabaString(match.LeagueName, match.LeagueGroupId);
		});
		return matchGroup;
	}

	useEffect(() => {
		itemsEmptyCheck(oddsData?.MatchCollection)
			.fold(
				() => {
					setLeagueCollection([])
				},
				() => {
					IO.of(oddsData?.MatchCollection)
						.map((matchCollection: SportMatchModel[]) => getSortedMatchCollection(matchCollection, sortNames))
						.map(getGroupLeagues(oddsData?.leagueNameMap))
						.map(filterSabaStringToLeague)
						.map(setLeagueCollection)
						.run()
				}
			)
	}, [oddsData?.MatchCollection, user?.UserSetting.EventSortType])

	useEffect(() => {
		setOpenIndex(leagueCollection?.findIndex(x => x.Matches.some(y => y.MatchId === matchId)));
	}, [leagueCollection]);
	
	return (
		<div
			className="select-match scrollbar"
			data-open={isListOpen}
			ref={fastChangeTips}
		>
			<div className="select-match__wrap">
				{
					leagueCollection?.map((league, index) => {
						return (
							<div
								className="select-match__list"
								key={`${league.LeagueId}-${index}`}
								data-open={openIndex === index}
								onClick={() => handleLeagueOpen(index)}
							>
								<div className="select-match__league" >
									<span>{league.LeagueName}</span>
									<DropdownIcon />
								</div>
								<div className="select-match__sub-list" >
									<TopbarMatchListOption
										matchId={matchId}
										oddsData={oddsData}
										matchList={league.Matches}
									/>
								</div>
							</div>
						)
					})
				}
			</div>
		</div>
	);
};